/**
 * VIB-2091: felles merkevare-tokens for budsjett-eksport (PNG + Keynote/PPTX).
 * Matcher Sølvrev brand guide: Reveblå/Revemørk/Revegrå + Clash Display/Grotesk.
 * True `.key` kan ikke genereres i nettleseren; PPTX med native tabell åpnes i Keynote.
 */

export const OFFER_EXPORT_COLORS = {
  dark: "020A1F",
  darkText: "FFFFFF",
  mutedText: "8A94A6",
  tableBg: "DDE2E2",
  tableText: "081F3B",
  tableMuted: "5A6878",
  tableBorder: "081F3B",
  rosa: "FF8888",
  reveblaa: "031EFF",
} as const;

export const OFFER_EXPORT_HEX = {
  dark: "#020A1F",
  darkText: "#FFFFFF",
  mutedText: "#8A94A6",
  tableBg: "#DDE2E2",
  tableText: "#081F3B",
  tableMuted: "#5A6878",
  tableBorder: "#081F3B",
  rosa: "#FF8888",
  reveblaa: "#031EFF",
} as const;

/**
 * Offisiell 8-takket asterisk fra `src/assets/icons/revebla/rosa_asterisk_blue.svg`.
 * html2canvas trenger inline <svg>, ikke data-URI i <img>.
 *
 * Selve figuren opptar bare 30–70 i det opprinnelige 0 0 100 100-rommet,
 * altså 40 % av bredden. Tegnet med den viewBoxen ble ikonet under
 * halvparten av oppgitt størrelse, mens malens `asterisk_ouline_poppink.png`
 * er tett innrammet. ASTERISK_VIEWBOX rammer derfor figuren inn, med én
 * enhet klaring hele veien rundt så konturen ikke klippes (VIB-2556).
 */
export const ASTERISK_VIEWBOX = "29 29 42 42";
export const ASTERISK_POLYGON_POINTS =
  "66.971 38.686 61.314 33.029 54 40.343 54 30 46 30 46 40.343 38.686 33.029 33.029 38.686 40.343 46 30 46 30 54 40.343 54 33.029 61.314 38.686 66.971 46 59.657 46 70 54 70 54 59.657 61.314 66.971 66.971 61.314 59.657 54 70 54 70 46 59.657 46 66.971 38.686";

/**
 * Strektykkelse på asterisken, i viewBox-enheter. Malen bruker
 * `asterisk_ouline_poppink.png` — en KONTUR, ikke en fylt form. Målt på
 * malens egen fil er streken ~4,6 % av ikonets bredde, og asterisken er
 * 40 enheter bred i viewBoxen (VIB-2556).
 */
export const ASTERISK_STROKE_WIDTH = 2;

export const ASTERISK_ROSA_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${ASTERISK_VIEWBOX}"><polygon fill="none" stroke="#FF8888" stroke-width="${ASTERISK_STROKE_WIDTH}" stroke-linejoin="miter" points="${ASTERISK_POLYGON_POINTS}"/></svg>`;

/** Reven uten ring — til Reveblå-sirkel. Path fra `src/assets/logo-white.svg`. */
export const FOX_FACE_PATH =
  "M67.049,42.974c-1.536-3.886-2.49-6.807-2.913-8.934l-.737-3.714-2.285,3.052c-3.636,4.851-4.622,8.711-4.86,10.863h-7.893v.023h-4.496c-.237-2.152-1.223-6.011-4.859-10.863l-2.285-3.052-.738,3.714c-.422,2.125-1.376,5.048-2.912,8.934-.155.397-.308.779-.456,1.151-1.545,3.881-2.564,6.447-2.564,8.92,0,4.899,3.382,8.553,10.052,10.861,6.764,2.34,8.582,3.473,8.854,3.659l1.221,1.263,1.005-1.279c.307-.208,2.16-1.356,8.834-3.666,6.67-2.31,10.052-5.964,10.052-10.861,0-2.474-1.022-5.039-2.565-8.92-.145-.372-.298-.756-.455-1.151Z";

/**
 * Tight viewBox rundt reven (logoen er 100×100 med ring rundt). Uten crop
 * blir fjeset ~8 px i 48 px-sirkelen og forsvinner mot Reveblå.
 */
export const FOX_FACE_VIEWBOX = "30 28 40 44";

export const FOX_WHITE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${FOX_FACE_VIEWBOX}"><path fill="#fff" d="${FOX_FACE_PATH}"/></svg>`;

/**
 * SVG som base64 data-URI.
 *
 * pptxgenjs avviser en `charset=utf-8,<urlencoded>`-URI med «Image `data`
 * value lacks a base64 header!» og hopper over bildet uten å feile
 * eksporten. Resultatet er en Keynote-fil der asterisken og reven mangler,
 * men som lastes ned som om alt gikk bra. Derfor base64.
 */
export function svgDataUrl(svg: string): string {
  // SVG-ene våre er ASCII, men TextEncoder-veien er trygg også for tegn
  // over 0xFF, der btoa ellers kaster.
  const bytes = new TextEncoder().encode(svg);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

/**
 * Fontnavn for .pptx-eksporten.
 *
 * pptxgenjs kan ikke embedde fonter — vi sender bare et navn, og Keynote
 * matcher det mot fontene som er installert på maskinen som åpner fila.
 * VIB-2556 valgte derfor Helvetica, så alle fikk NOE forutsigbart. Erik
 * 29.09 overstyrte: Sølvrev har Clash installert, og fila er deres eget
 * arbeidsdokument, ikke noe kunden åpner.
 *
 * NAVNET MÅ VÆRE FAMILIEN, IKKE FAMILIE + VEKT. Dette tok to forsøk å
 * treffe, fordi Clash finnes i to ulike pakkinger:
 *
 * - Webfilene og løse .ttf-er fra Fontshare bruker én familie per vekt;
 *   der er familienavnet (name ID 1) «Clash Grotesk Semibold».
 * - Desktop-installasjonen på Sølvrevs Mac-er er pakket normalt: familien
 *   heter «Clash Display» / «Clash Grotesk», og Semibold er en STIL inni
 *   den (Font Book viser «2 stiler: Medium, Halvfet»).
 *
 * Vi bygget først på .ttf-en og sendte «Clash Grotesk Semibold» som
 * familienavn. Keynote leste det som familie «Clash Grotesk» + stil
 * «Semibold», fant familien, men ikke stilen, og meldte fonten som
 * manglende (Erik 30.09). Vi sender derfor familien, og lar `bold` velge
 * den tyngste stilen i den — samme mekanikk som i enhver annen app.
 */
export const OFFER_EXPORT_FONT_DISPLAY = "Clash Display";
export const OFFER_EXPORT_FONT_BODY = "Clash Grotesk";

/** CSS-familier som matcher @font-face i src/index.css (PNG/html2canvas). */
export const OFFER_EXPORT_FONT_DISPLAY_CSS =
  "ClashDisplay, 'Clash Display', Helvetica, Arial, sans-serif";
export const OFFER_EXPORT_FONT_BODY_CSS =
  "ClashGrotesk, 'Clash Grotesk', Helvetica, Arial, sans-serif";

/**
 * Halvfet i PNG-en. `src/index.css` deklarerer Clash i 400, 500 og 600 —
 * det finnes ingen 700-fil. Ba vi om 700 lagde nettleseren en FALSK bold
 * ved å tykne Regular, som er grunnen til at eksporten ikke så ut som
 * Clash (Erik 29.09). Malen bruker selv ClashGrotesk-Semibold.
 */
export const OFFER_EXPORT_SEMIBOLD_CSS = 600;

/* -------------------------------------------------------------------------
 * Delt geometri for venstrepanelet.
 *
 * PNG-lerretet er 1280 × 720 for et 13,333 × 7,5"-lysbilde, altså nøyaktig
 * 96 px per tomme. Der et mål er det SAMME i begge eksportene oppgis det
 * derfor i begge enheter her, slik at PPTX-en og PNG-en ikke kan drifte fra
 * hverandre — de skal se like ut.
 *
 * Erik 29.09: asterisken var for stor, og hele tekstblokken (både
 * hovedteksten og den vertikale overskriften) lå for høyt. Malens egne mål
 * fra VIB-2556 (0,44" ikon, tekst fra 0,425") er derfor bevisst forlatt.
 * ---------------------------------------------------------------------- */

/** Asterisken øverst til venstre. Var 0,44" / 42 px. */
export const OFFER_EXPORT_ASTERISK_IN = 0.3;
export const OFFER_EXPORT_ASTERISK_PX = 29;

/**
 * Den loddrette aksen asterisken og «budsjett_» skal FLUKTE på.
 *
 * Begge sto tidligere med hardkodet venstrekant, kalibrert for den gamle
 * 0,745" brede asterisken. Da den ble krympet til 0,30" (Erik 29.09)
 * beholdt vi venstrekanten, så senteret gled 0,22" til venstre og flukten
 * røk. Posisjonene regnes nå UT FRA aksen, slik at neste
 * størrelsesendring ikke kan gjenta feilen.
 *
 * Målt i malens egen `preview.jpg`: asterisken har senter 1,107" og
 * «budsjett_» 1,055" — samme akse innenfor målestøyen i en JPEG.
 */
export const OFFER_EXPORT_MARK_AXIS_IN = 1.11;
export const OFFER_EXPORT_MARK_AXIS_PX = 107;

/**
 * Tekstblokken i venstrepanelet SENTRERES vertikalt, den ankres ikke i
 * toppen. Malen fyller panelet med en lang vilkårstekst; med vår korte
 * standardtekst ga den samme topp-ankeringen et tungt toppfelt og et tomt
 * bunnfelt, som er det Erik reagerte på. Sentrering treffer begge: kort
 * tekst faller ned mot midten, lang tekst fyller som i malen.
 *
 * Verdien her er bare gulvet — teksten går aldri høyere enn dette, uansett
 * hvor lang den blir.
 */
export const OFFER_EXPORT_LEFT_TEXT_MIN_TOP_IN = 0.6;

/**
 * Den roterte «budsjett_» skal ende ca. 0,77" over bunnkanten, som i malen.
 * Den sto ~1,6" for høyt.
 *
 * Verdien MÅ oppgis per rendrer: de to posisjonerer rotert tekst fra hvert
 * sitt anker — PPTX fra tekstboksens topp (boksen roteres om sitt senter),
 * PNG fra panelets bunn (rotasjon om nedre venstre hjørne). Endrer du den
 * ene, mål den andre på nytt i stedet for å regne om.
 */
export const OFFER_EXPORT_VERTICAL_HEAD_Y_IN = 5.85;
export const OFFER_EXPORT_VERTICAL_HEAD_BOTTOM_PX = 74;

/**
 * Skillelinjene i budsjett-tabellen. ÉN vekt overalt.
 *
 * Målt i malens `preview.jpg`: den har nøyaktig fire linjer, alle 1 px på
 * 76,8 px/tomme (0,94 pt) og alle nær svarte. SUM skilles ikke ut med en
 * tyngre strek, men ved å ha linje både over og under. Vår 1 pt mot 1,5 pt
 * ga i stedet tynne, lyse fase-linjer mot tykke, svarte SUM-linjer, som er
 * det Erik reagerte på 29.09.
 */
export const OFFER_EXPORT_RULE_PT = 1;
export const OFFER_EXPORT_RULE_PX = 1;

/**
 * Luft mellom siste rad i en fasegruppe og linja under den (PNG).
 *
 * Radene er stramme (VIB-2556 senket paddingen for å treffe malens
 * tetthet), så radgrensen — der linja tegnes — lå nede i underlengdene på
 * g, j og y. Linja gikk derfor tvers gjennom teksten i stedet for å stå
 * under den. Fase-headeren har allerede luft PÅ SIN side av linja.
 */
export const OFFER_EXPORT_RULE_GAP_PX = 5;
